/**
 * Wave-75 — vendor portal unread-messages count, polled every 30s.
 *
 * The hook returns the current unread count from
 * GET /api/v1/vendor/messages/unread-count and refetches automatically
 * (also on window focus). Used by the sidebar nav to render a small
 * badge next to "Messages" when there are unread admin replies.
 *
 * Stale-while-revalidate is fine — the badge can be off by ~30s and
 * nobody cares. When the user opens /messages we mark-read which
 * invalidates the query → badge updates instantly.
 */
import { useQuery } from '@tanstack/react-query';
import { messagesApi } from '../api/messages';
import { useAuth } from '../contexts/AuthContext';

export function useMessagesUnread(enabled = true) {
  const { isAuthenticated } = useAuth();
  const q = useQuery({
    queryKey: ['messages', 'unread'],
    queryFn: () => messagesApi.unreadCount(),
    enabled: isAuthenticated && enabled,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    // 5s stale time so toggling between tabs doesn't fire a refetch
    // for every navigation event.
    staleTime: 5_000,
  });
  return enabled ? q.data ?? 0 : 0;
}
